import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import { siteConfig } from '../../config/site'
import { navigation, storeHours, utilityNavigation } from '../../data'
import { whatsNewItems } from '../../data/announcements'
import { SearchButton } from '../search/SiteSearch'
import type { NavigationItem } from '../../data/types'

function UtilityLinks({ onNavigate }: { onNavigate: () => void }) {
  return <>{utilityNavigation.map((item) => item.external
    ? <a key={item.href} href={item.href} onClick={onNavigate}>{item.label}</a>
    : <Link key={item.href} to={item.href} onClick={onNavigate}>{item.label}</Link>)}</>
}

function Chevron() {
  return <svg className="header-chevron" viewBox="0 0 12 8" aria-hidden="true"><path d="m2 2 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
}

export function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [openCategory, setOpenCategory] = useState<string | null>(null)
  const [now, setNow] = useState(() => new Date())
  const header = useRef<HTMLElement>(null)
  const menuButton = useRef<HTMLButtonElement>(null)
  const categoryButtons = useRef<Record<string, HTMLButtonElement | null>>({})
  const location = useLocation()
  const closeMenus = () => { setMenuOpen(false); setOpenCategory(null) }

  useEffect(() => {
    const update = () => setNow(new Date())
    const timer = setInterval(update, 60_000)
    window.addEventListener('focus', update)
    return () => { clearInterval(timer); window.removeEventListener('focus', update) }
  }, [])
  useEffect(() => {
    const onOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !header.current?.contains(event.target)) closeMenus()
    }
    const onPopState = () => closeMenus()
    const breakpoint = window.matchMedia('(max-width: 1120px)')
    const onResize = () => closeMenus()
    document.addEventListener('pointerdown', onOutside)
    window.addEventListener('popstate', onPopState)
    breakpoint.addEventListener('change', onResize)
    return () => {
      document.removeEventListener('pointerdown', onOutside)
      window.removeEventListener('popstate', onPopState)
      breakpoint.removeEventListener('change', onResize)
    }
  }, [])

  const weekday = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', weekday: 'long' }).format(now)
  const hours = storeHours.find((entry) => entry.days.includes(weekday))
  const isActive = (item: NavigationItem) => [item.href, ...(item.children?.map((child) => child.href) ?? [])]
    .some((href) => location.pathname.toLowerCase() === href.split('?')[0].toLowerCase())

  return <header className="site-header refreshed-header" ref={header} onKeyDown={(event) => {
    if (event.key !== 'Escape') return
    event.preventDefault()
    if (openCategory) {
      categoryButtons.current[openCategory]?.focus()
      setOpenCategory(null)
    } else if (menuOpen) { setMenuOpen(false); menuButton.current?.focus() }
  }} onBlur={(event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) closeMenus()
  }}>
    <a className="skip-link" href="#content" onClick={closeMenus}>Skip to Main Content</a>
    <div className="header-brand-row inner-shell">
      <Link className="header-logo" to="/" aria-label={`${siteConfig.displayName} home`} onClick={closeMenus}>
        <img src="/images/pack-print-mail-logo.jpg" alt="Pack Print Mail — Pack it. Print it. Mail it." />
      </Link>
      <div className="header-information">
        <div className="header-store-details">
          <a className="header-phone" href={siteConfig.phoneHref}>{siteConfig.phone}</a>
          <a className="header-address" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(siteConfig.address.formatted)}`}>{siteConfig.address.street}<br />{' '}{siteConfig.address.locality}, {siteConfig.address.region} {siteConfig.address.postalCode}</a>
          <Link className="header-hours" to="/contact-us#store-hours" onClick={closeMenus}><strong>Today</strong><span>{hours?.display ?? 'Call for hours'}</span></Link>
        </div>
      </div>
    </div>
    <nav className="header-service-nav" aria-label="Main menu">
      <div className="inner-shell header-nav-shell">
        <div className="header-mobile-toolbar"><button ref={menuButton} className="header-menu-toggle" type="button" aria-expanded={menuOpen} aria-controls="header-navigation" onClick={() => { setMenuOpen((value) => !value); setOpenCategory(null) }}><span aria-hidden="true">☰</span> Menu</button><SearchButton className="search-mobile" onOpen={closeMenus} /></div>
        <div className={`header-navigation${menuOpen ? ' is-open' : ''}`} id="header-navigation">
          <p className="header-mobile-label">Services</p>
          <ul className="header-category-list">{navigation.map((item, index) => <li key={item.href} className={isActive(item) ? 'is-active' : ''}>
            {index === 4 && <span className="header-mobile-divider" />}
            {item.children?.length ? <>
              <button ref={(element) => { categoryButtons.current[item.label] = element }} type="button" aria-expanded={openCategory === item.label} aria-controls={`header-submenu-${index}`} onClick={() => setOpenCategory((value) => value === item.label ? null : item.label)}>{item.label}<Chevron /></button>
              <ul id={`header-submenu-${index}`} className="header-submenu" hidden={openCategory !== item.label}>{item.children.map((child) => <li key={`${child.href}-${child.label}`}><NavLink to={child.href} end onClick={closeMenus}>{child.label}</NavLink></li>)}</ul>
            </> : <NavLink to={item.href} onClick={closeMenus}>{item.label}</NavLink>}
          </li>)}</ul>
          <div className="header-mobile-utilities"><p className="header-mobile-label">Quick Links</p><UtilityLinks onNavigate={closeMenus} /></div>
          <div className="header-search-actions"><SearchButton className="search-desktop" onOpen={closeMenus} /><a className="header-order" href={siteConfig.onlinePrintShopUrl} onClick={closeMenus}>Order Online</a></div>
        </div>
      </div>
    </nav>
    <div className="header-promotion inner-shell" aria-label="What's new">
      <strong>What’s New</strong>{whatsNewItems.map((item) => <div key={item.href}><span>{item.title}</span><Link to={item.href} onClick={closeMenus}>Learn More <span aria-hidden="true">→</span></Link></div>)}
    </div>
  </header>
}
